import { useState } from 'react'
import { trackEvent } from '@/shared/api/telemetry'
import { type CreateLeadInput, useCreateLead } from '@/shared/api/leads'
import type { Lead } from '@/shared/types'
import { type ExistingLead, existingLeadOf } from '@/features/lead-management/duplicates'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel, FieldLegend, FieldSet } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

// Manual "add a lead" form. Creates the company (+ optional primary contact)
// and the crm_leads row via POST /api/leads, then hands the created lead back
// so the caller can open it. Idempotent on org number — if the company is
// already in the CRM the server returns the existing lead (created=false) and
// we open that instead of duplicating it. Without an org number, a company
// with the same name and country answers 409 "Finns redan"; the notice offers
// to open that lead or to add this one anyway.
export default function AddLeadModal({
  onClose,
  onCreated,
  onOpenExisting,
}: {
  onClose: () => void
  onCreated: (lead: Lead, created: boolean) => void
  onOpenExisting: (id: string) => void
}) {
  const create = useCreateLead()
  const [f, setF] = useState({
    company: '',
    orgNo: '',
    city: '',
    country: 'SE',
    website: '',
    employees: '',
    contactName: '',
    contactRole: '',
    contactEmail: '',
    contactPhone: '',
  })
  const [err, setErr] = useState<string | null>(null)
  const [existing, setExisting] = useState<ExistingLead | null>(null)
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setExisting(null)
    setF(prev => ({ ...prev, [k]: e.target.value }))
  }

  const submit = (allowDuplicate = false) => {
    setErr(null)
    setExisting(null)
    const company = f.company.trim()
    if (!company) {
      setErr('Företagsnamn är obligatoriskt.')
      return
    }
    const employees = f.employees.trim() ? Number(f.employees.trim()) : undefined
    if (employees !== undefined && (!Number.isFinite(employees) || employees < 0)) {
      setErr('Antal anställda måste vara ett positivt tal.')
      return
    }
    const contactName = f.contactName.trim()
    const body: CreateLeadInput = {
      company,
      orgNo: f.orgNo.trim() || undefined,
      city: f.city.trim() || undefined,
      country: f.country.trim().toUpperCase() || 'SE',
      website: f.website.trim() || undefined,
      employees,
      contact: contactName
        ? {
            name: contactName,
            role: f.contactRole.trim() || undefined,
            email: f.contactEmail.trim() || undefined,
            phone: f.contactPhone.trim() || undefined,
          }
        : undefined,
      allowDuplicate: allowDuplicate || undefined,
    }
    create.mutate(body, {
      onSuccess: ({ lead, created }) => {
        trackEvent('add_lead_submitted', undefined, lead.id)
        onCreated(lead, created)
      },
      onError: x => {
        const match = existingLeadOf(x)
        if (match) setExisting(match)
        else setErr((x as Error).message || 'Kunde inte lägga till leaden.')
      },
    })
  }

  return (
    <Dialog open onOpenChange={next => { if (!next) onClose() }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Lägg till en lead</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <FieldGroup>
            <FieldSet>
              <FieldLegend variant="legend">Företag</FieldLegend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field className="sm:col-span-2">
                  <FieldLabel htmlFor="add-lead-company">Namn</FieldLabel>
                  <Input
                    id="add-lead-company"
                    autoFocus
                    value={f.company}
                    onChange={set('company')}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit() }}
                    placeholder="Acme AB"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-orgno">Org.nr</FieldLabel>
                  <Input id="add-lead-orgno" value={f.orgNo} onChange={set('orgNo')} placeholder="556xxx-xxxx" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-city">Ort</FieldLabel>
                  <Input id="add-lead-city" value={f.city} onChange={set('city')} placeholder="Stockholm" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-country">Land</FieldLabel>
                  <Input
                    id="add-lead-country"
                    value={f.country}
                    onChange={set('country')}
                    maxLength={2}
                    placeholder="SE"
                    className="uppercase"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-employees">Anställda</FieldLabel>
                  <Input id="add-lead-employees" value={f.employees} onChange={set('employees')} inputMode="numeric" placeholder="—" />
                </Field>
                <Field className="sm:col-span-2">
                  <FieldLabel htmlFor="add-lead-website">Webbplats</FieldLabel>
                  <Input id="add-lead-website" value={f.website} onChange={set('website')} placeholder="https://" />
                </Field>
              </div>
            </FieldSet>

            <FieldSet>
              <FieldLegend variant="legend">Primär kontakt (valfritt)</FieldLegend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field>
                  <FieldLabel htmlFor="add-lead-contact-name">Namn</FieldLabel>
                  <Input id="add-lead-contact-name" value={f.contactName} onChange={set('contactName')} placeholder="Fullständigt namn" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-contact-role">Roll</FieldLabel>
                  <Input id="add-lead-contact-role" value={f.contactRole} onChange={set('contactRole')} placeholder="VD, CFO…" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-contact-email">E-post</FieldLabel>
                  <Input id="add-lead-contact-email" value={f.contactEmail} onChange={set('contactEmail')} placeholder="name@company.se" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="add-lead-contact-phone">Telefon</FieldLabel>
                  <Input id="add-lead-contact-phone" value={f.contactPhone} onChange={set('contactPhone')} placeholder="+46…" />
                </Field>
              </div>
            </FieldSet>

            {existing && (
              <Alert variant="warning">
                <AlertDescription>
                  <p className="m-0">
                    {existing.existingName || f.company.trim()} finns redan (ägare {existing.owner})
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => onOpenExisting(existing.existingId)}>
                      Öppna
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => submit(true)} disabled={create.isPending}>
                      Lägg till ändå
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            {err && <FieldError>{err}</FieldError>}
          </FieldGroup>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Avbryt</Button>
          <Button
            variant="yellow"
            onClick={() => submit()}
            disabled={create.isPending || !f.company.trim()}
            loading={create.isPending}
          >
            Lägg till lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
